import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DateInput from '../components/DateInput';
import { usePermissions } from '../context/PermissionsContext';

interface Policy {
  id: number;
  policy_number: string;
  product_type: string;
  status: string;
}

interface Employee {
  id: number;
  name: string;
  surname: string;
  email: string;
  active?: boolean;
}

interface Client {
  id: number;
  name: string;
  surname: string;
  email: string;
  mobile_number?: string;
  client_number?: string;
}

const CreateClaim: React.FC = () => {
  const { can } = usePermissions();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [client, setClient] = useState<{
  id: string;
  name: string;
  surname: string;
  client_number: string;
} | null>(null);
  
  // Data state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedPolicyProducts, setSelectedPolicyProducts] = useState<any[]>([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    policyId: '',
    productId: '',
    claimMethod: '',
    claimType: '',
    description: '',
    assignedTo: '',
    claimsReportedDate: new Date().toISOString().split('T')[0],
    claimDate: new Date().toISOString().split('T')[0],
    underwritersClaimNumber: '',
    totalCostOfClaim: '',
    claimExcess: '',
  });

  // Static options
  const claimMethods = [
    { value: 'face_to_face', label: 'Face To Face' },
    { value: 'email', label: 'E-Mail' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'whatsapp', label: 'Whatsapp' }
  ];

  const claimTypes = [
    { value: 'total_loss', label: 'Total Loss' },
    { value: 'partial_loss', label: 'Partial Loss' }
  ];

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchUserAndClient();
    fetchPolicies();
    fetchConsultants();
  }, [clientId]);

  const fetchUserAndClient = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    if (clientId) {
      const clientRes = await axios.get(`${API_URL}/api/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Map the response to match Header's expected format
      const clientData = clientRes.data;
      setClient({
        id: String(clientData.id),
        name: clientData.name || '',
        surname: clientData.surname || '',
        client_number: clientData.client_number || clientData.id || '',
      });
    }
  } catch (err) {
    console.error('Failed to fetch client:', err);
  }
};

  const fetchPolicies = async () => {
    setIsLoadingPolicies(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/claims/policies?clientId=${clientId}&status=active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let policiesData = [];
      if (Array.isArray(response.data)) {
        policiesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        policiesData = response.data.data;
      }
      
      setPolicies(policiesData);
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  const fetchPolicyProducts = async (policyId: string) => {
    if (!policyId) {
      setSelectedPolicyProducts([]);
      return;
    }
    
    setIsLoadingProducts(true);
    setSelectedPolicyProducts([]);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      let allProducts: any[] = [];

      const policyResponse = await axios.get(
        `${API_URL}/api/policies/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const policyData = policyResponse.data.data || policyResponse.data;
      console.log('📦 Policy data:', policyData);
      
      if (policyData.quote_id) {
        const quoteId = policyData.quote_id.toString();
        console.log(`📦 Fetching products from quote: ${quoteId}`);
        
        const endpoints = [
          { name: 'properties', url: `${API_URL}/api/properties?quote_id=${quoteId}` },
          { name: 'vehicles', url: `${API_URL}/api/vehicles?quote_id=${quoteId}` },
          { name: 'household-contents', url: `${API_URL}/api/household-contents?quote_id=${quoteId}` },
          { name: 'risk-items', url: `${API_URL}/api/risk-items?quote_id=${quoteId}` },
          { name: 'water-craft', url: `${API_URL}/api/water-craft?quote_id=${quoteId}` },
          { name: 'trailer-caravans', url: `${API_URL}/api/trailer-caravans?quote_id=${quoteId}` },
          { name: 'value-added', url: `${API_URL}/api/value-added?quote_id=${quoteId}` }
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await axios.get(endpoint.url, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            let productData = [];
            if (Array.isArray(response.data)) {
              productData = response.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
              productData = response.data.data;
            }
            
            if (productData.length > 0) {
              console.log(`✅ Found ${productData.length} ${endpoint.name}`);
              productData.forEach((product: any) => {
                let productName = getProductDisplayName(product, endpoint.name);
                allProducts.push({
                  id: product.id,
                  product_id: product.id,
                  product_name: productName,
                  name: productName,
                  product_type: endpoint.name,
                  quote_id: quoteId,
                  uniqueKey: `${endpoint.name}-${product.id}`,
                  ...product
                });
              });
            }
          } catch (err: any) {
            if (err.response && err.response.status === 404) {
              console.log(`ℹ️ No ${endpoint.name} found`);
            } else {
              console.log(`⚠️ Error fetching ${endpoint.name}:`, err.message);
            }
          }
        }
        
        const seen = new Set();
        const uniqueProducts = allProducts.filter((product) => {
          const key = product.uniqueKey || `${product.product_type}-${product.id}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        
        console.log(`📊 Total products found: ${uniqueProducts.length}`);
        setSelectedPolicyProducts(uniqueProducts);
        setIsLoadingProducts(false);
        return;
        
      } else {
        console.log('🔄 No quote_id, fetching products from policy tables and localStorage');
        setSelectedPolicyProducts([]);
        setIsLoadingProducts(false);
        return;
      }
      
    } catch (error) {
      console.error('Error fetching policy products:', error);
      setSelectedPolicyProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const getProductDisplayName = (product: any, type: string): string => {
    let productName = '';
    
    if (type === 'properties') {
      const address = product.address_line_1 || product.addressLine1 || '';
      const propertyType = product.property_type || product.propertyType || '';
      productName = propertyType && address ? `${propertyType} - ${address}` : propertyType || address || `Property ${product.id}`;
    } else if (type === 'household-contents') {
      const description = product.description || '';
      const address = product.address_line_1 || product.addressLine1 || '';
      productName = description || `Household Contents - ${address}` || `Household Contents ${product.id}`;
    } else if (type === 'vehicles') {
      const make = product.make || '';
      const model = product.model || '';
      productName = make && model ? `${make} ${model}` : `Vehicle ${product.id}`;
    } else if (type === 'trailer-caravans') {
      productName = product.description || product.type || `Trailer Caravan ${product.id}`;
    } else if (type === 'risk-items') {
      productName = product.description || product.itemName || product.item_name || `Risk Item ${product.id}`;
    } else if (type === 'value-added') {
      productName = product.description || product.name || `Value Added ${product.id}`;
    } else if (type === 'water-craft') {
      productName = product.vesselName || product.vessel_name || product.description || `Water Craft ${product.id}`;
    }
    
    return productName.trim() || `${type} ${product.id}`;
  };

    const fetchConsultants = async () => {
    setIsLoadingEmployees(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/consultants`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let consultantsData = [];
      if (Array.isArray(response.data)) {
        consultantsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        consultantsData = response.data.data;
      }
      
      const activeConsultants = consultantsData.filter((c: any) => c.active !== false);
      setEmployees(activeConsultants);
    } catch (error) {
      console.error('Error fetching consultants:', error);
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: string, field: string) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.claimMethod || !formData.claimType || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const productId = formData.productId ? parseInt(formData.productId) : null;
      const totalCost = formData.totalCostOfClaim ? parseFloat(formData.totalCostOfClaim) : null;
      const claimExcess = formData.claimExcess ? parseFloat(formData.claimExcess) : null;

      const payload = {
        client_id: parseInt(clientId!),
        policy_id: formData.policyId ? parseInt(formData.policyId) : null,
        product_id: productId,
        claim_method: formData.claimMethod,
        claim_type: formData.claimType,
        description: formData.description,
                consultant_id: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        status: 'pending',
        date_received: formData.claimDate || new Date().toISOString().split('T')[0],
        claims_reported_date: formData.claimsReportedDate || null,
        underwriters_claim_number: formData.underwritersClaimNumber || null,
        total_cost_of_claim: totalCost,
        claim_excess: claimExcess,
      };

      const response = await axios.post(
        `${API_URL}/api/claims`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Claim created:', response.data);
      setSuccess('Claim created successfully!');
      
      // Navigate back to client page with claims tab active
      setTimeout(() => {
        navigate(`/personal/${clientId}`, { 
          state: { activeTab: 'claims' }
        });
      }, 1500);
      
    } catch (error: any) {
      console.error('Error creating claim:', error);
      setError(error.response?.data?.message || 'Failed to create claim');
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

    if (!can('claims', 'write')) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to create Claims.</p>
      </div>
    );
  }


  if (!user) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Create New Claim"
          user={user}
          client={client}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={!!client}
        />
        <div className="flex-1 p-6">
          <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Create New Claim</h2>
              <button
                onClick={() => navigate(`/personal/${clientId}`, { state: { activeTab: 'claims' } })}
                className="text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Select Policy */}
                <div>
                  <label htmlFor="policyId" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Policy <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <select
                    id="policyId"
                    name="policyId"
                    value={formData.policyId}
                    onChange={(e) => {
                      const selectedPolicyId = e.target.value;
                      setFormData(prev => ({ ...prev, policyId: selectedPolicyId, productId: '' }));
                      setSelectedPolicyProducts([]);
                      if (selectedPolicyId) {
                        fetchPolicyProducts(selectedPolicyId);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoadingPolicies}
                  >
                    <option value="">No policy selected</option>
                    {policies.map(policy => (
                      <option key={policy.id} value={policy.id.toString()}>
                        {policy.policy_number}
                      </option>
                    ))}
                  </select>
                  {isLoadingPolicies && (
                    <p className="text-xs text-blue-600 mt-1">Loading policies...</p>
                  )}
                </div>

                {/* Select Product */}
                <div>
                  <label htmlFor="productId" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Product <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <select
                    id="productId"
                    name="productId"
                    value={formData.productId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoadingProducts || !formData.policyId}
                  >
                    <option value="">
                      {!formData.policyId ? 'Please select a policy first' : 
                       isLoadingProducts ? 'Loading products...' : 
                       selectedPolicyProducts.length === 0 ? 'No products found' : 
                       'Select a product'}
                    </option>
                    {selectedPolicyProducts.map((product) => {
                      const productId = product.id || product.product_id;
                      const productName = product.product_name || product.name || product.product_type || `Product ${product.id}`;
                      return (
                        <option 
                          key={product.uniqueKey || `product-${productId}-${product.product_type}`} 
                          value={String(productId)}
                        >
                          {productName}
                        </option>
                      );
                    })}
                  </select>
                  {isLoadingProducts && (
                    <p className="text-xs text-blue-600 mt-1">Loading products...</p>
                  )}
                </div>

                {/* Select Claim Method */}
                <div>
                  <label htmlFor="claimMethod" className="block text-sm font-medium text-gray-700 mb-1">
                    Method Received *
                  </label>
                  <select
                    id="claimMethod"
                    name="claimMethod"
                    value={formData.claimMethod}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select method received</option>
                    {claimMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select Claim Type */}
                <div>
                  <label htmlFor="claimType" className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Type *
                  </label>
                  <select
                    id="claimType"
                    name="claimType"
                    value={formData.claimType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select claim type</option>
                    {claimTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                                {/* Select Consultant */}
                <div>
                  <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Consultant <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <select
                    id="assignedTo"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoadingEmployees}
                  >
                                        <option value="">Select consultant</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name} {employee.surname}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description - Full width */}
                <div className="col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter claim description..."
                    required
                  />
                </div>

                {/* Claims Reported Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Claims Reported Date <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <DateInput
                    value={formData.claimsReportedDate || ''}
                    onChange={(date) => handleDateChange(date, 'claimsReportedDate')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Select claims reported date"
                  />
                </div>

                {/* Claim Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Date <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <DateInput
                    value={formData.claimDate || ''}
                    onChange={(date) => handleDateChange(date, 'claimDate')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Select claim date"
                  />
                </div>

                {/* Underwriters Claim Number */}
                <div>
                  <label htmlFor="underwritersClaimNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Underwriters Claim Number <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="underwritersClaimNumber"
                    name="underwritersClaimNumber"
                    value={formData.underwritersClaimNumber}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter underwriters claim number..."
                  />
                </div>

                {/* Total Cost of Claim */}
                <div>
                  <label htmlFor="totalCostOfClaim" className="block text-sm font-medium text-gray-700 mb-1">
                    Total Cost of Claim <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="number"
                      id="totalCostOfClaim"
                      name="totalCostOfClaim"
                      value={formData.totalCostOfClaim}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                {/* Claim Excess */}
                <div>
                  <label htmlFor="claimExcess" className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Excess <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="number"
                      id="claimExcess"
                      name="claimExcess"
                      value={formData.claimExcess}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate(`/personal/${clientId}`, { state: { activeTab: 'claims' } })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Claim'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClaim;